import React, { createContext, useContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LockKeyhole, Sparkles } from 'lucide-react';
import { paymentService } from '@/services/paymentService';
import { paymentAPI } from '@/api/payment';
import { 
  Subscription, 
  PricingPlan, 
  Invoice, 
  PaymentMethod, 
  BillingInterval,
  UsageMetrics,
  BillingState
} from '@/types/payment';
import { useAuth } from './AuthContext';
import { useToast } from '@/hooks/use-toast';
import { AI_ACCESS_PROMPT_EVENT, type AIAccessPromptReason } from '@/lib/aiAccess';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface PaymentContextType {
  // Subscription state
  subscription: Subscription | null;
  isLoadingSubscription: boolean;
  isOpeningCheckout: boolean;
  isOpeningPortal: boolean;
  refreshBilling: (checkoutSessionId?: string) => Promise<BillingState>;
  
  // Pricing plans
  pricingPlans: PricingPlan[];
  isLoadingPlans: boolean;
  
  // Invoices and payment methods
  invoices: Invoice[];
  paymentMethods: PaymentMethod[];
  isLoadingInvoices: boolean;
  isLoadingPaymentMethods: boolean;
  
  // Usage metrics
  usageMetrics: UsageMetrics | null;
  isLoadingUsage: boolean;
  
  // Actions
  createCheckoutSession: (planId: string, billingInterval: BillingInterval) => Promise<string>;
  openBillingPortal: () => Promise<void>;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  reactivateSubscription: (subscriptionId: string) => Promise<void>;
  updateSubscription: (subscriptionId: string, planId: string, billingInterval: BillingInterval) => Promise<void>;
  contactSales: (companyName: string, contactEmail: string, requirements: string) => Promise<void>;
  
  // Utility functions
  getCurrentPlan: () => PricingPlan | null;
  isSubscriptionActive: () => boolean;
  isSubscriptionTrialing: () => boolean;
  isSubscriptionPastDue: () => boolean;
  canAccessFeature: (feature: string) => boolean;
  getUsagePercentage: (metric: keyof UsageMetrics) => number;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

export const PaymentProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [aiAccessPrompt, setAiAccessPrompt] = useState<AIAccessPromptReason | null>(null);

  useEffect(() => {
    const onAIAccessPrompt = (event: Event) => {
      setAiAccessPrompt((event as CustomEvent<AIAccessPromptReason>).detail);
    };
    window.addEventListener(AI_ACCESS_PROMPT_EVENT, onAIAccessPrompt);
    return () => window.removeEventListener(AI_ACCESS_PROMPT_EVENT, onAIAccessPrompt);
  }, []);

  const refreshBilling = useCallback(async (checkoutSessionId?: string) => {
    const state = await paymentAPI.getBillingState(checkoutSessionId);
    await Promise.all(['subscription', 'invoices', 'payment-methods', 'usage-metrics', 'payment'].map(key =>
      queryClient.invalidateQueries({ queryKey: [key] })
    ));
    return state;
  }, [queryClient]);

  // Queries
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: () => paymentService.getCurrentSubscription(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // SSR and prerender-safe: Don't fetch during SSR/prerender
  const isClientOnly = typeof window !== 'undefined';
  const { data: pricingPlans = [], isLoading: isLoadingPlans } = useQuery({
    queryKey: ['pricing-plans'],
    queryFn: () => paymentService.getPricingPlans(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Prevent query from running during SSR, prerender, or before mount
    enabled: isClientOnly && !!user === !!user, // Always true on client, ensures hook rules are followed
  });

  const { data: invoices = [], isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['invoices', user?.id],
    queryFn: () => paymentService.getInvoices(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: paymentMethods = [], isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['payment-methods', user?.id],
    queryFn: () => paymentService.getPaymentMethods(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: usageMetrics, isLoading: isLoadingUsage } = useQuery({
    queryKey: ['usage-metrics', user?.id],
    queryFn: () => paymentService.getUsageMetrics(),
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Mutations
  const createCheckoutSessionMutation = useMutation({
    mutationFn: ({ planId, billingInterval }: { planId: string; billingInterval: BillingInterval }) =>
      paymentService.createCheckoutSession(planId, billingInterval),
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive",
      });
    },
  });

  const openBillingPortalMutation = useMutation({
    mutationFn: () => paymentService.createBillingPortalSession(),
    onSuccess: (portalSession) => {
      window.location.href = portalSession.url;
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to open billing portal",
        variant: "destructive",
      });
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: (subscriptionId: string) => paymentService.cancelSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast({
        title: "Success",
        description: "Continue managing your subscription in Stripe.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
    },
  });

  const reactivateSubscriptionMutation = useMutation({
    mutationFn: (subscriptionId: string) => paymentService.reactivateSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast({
        title: "Success",
        description: "Continue managing your subscription in Stripe.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reactivate subscription",
        variant: "destructive",
      });
    },
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: ({ subscriptionId, planId, billingInterval }: { 
      subscriptionId: string; 
      planId: string; 
      billingInterval: BillingInterval 
    }) => paymentService.updateSubscription(subscriptionId, planId, billingInterval),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast({
        title: "Success",
        description: "Continue managing your subscription in Stripe.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update subscription",
        variant: "destructive",
      });
    },
  });

  const contactSalesMutation = useMutation({
    mutationFn: ({ companyName, contactEmail, requirements }: {
      companyName: string;
      contactEmail: string;
      requirements: string;
    }) => paymentService.contactSales(companyName, contactEmail, requirements),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Continue your inquiry on the Contact Sales page.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send sales inquiry",
        variant: "destructive",
      });
    },
  });

  // Action functions
  const createCheckoutSession = async (planId: string, billingInterval: BillingInterval) => {
    const checkoutSession = await createCheckoutSessionMutation.mutateAsync({ planId, billingInterval });
    if (!checkoutSession.url) throw new Error('Stripe Checkout is unavailable. Please refresh and try again.');
    window.location.assign(checkoutSession.url);
    return checkoutSession.id;
  };

  const openBillingPortal = async () => {
    await openBillingPortalMutation.mutateAsync();
  };

  const cancelSubscription = async (subscriptionId: string) => {
    await cancelSubscriptionMutation.mutateAsync(subscriptionId);
  };

  const reactivateSubscription = async (subscriptionId: string) => {
    await reactivateSubscriptionMutation.mutateAsync(subscriptionId);
  };

  const updateSubscription = async (subscriptionId: string, planId: string, billingInterval: BillingInterval) => {
    await updateSubscriptionMutation.mutateAsync({ subscriptionId, planId, billingInterval });
  };

  const contactSales = async (companyName: string, contactEmail: string, requirements: string) => {
    await contactSalesMutation.mutateAsync({ companyName, contactEmail, requirements });
  };

  // Utility functions
  const getCurrentPlan = (): PricingPlan | null => {
    const paid = subscription && ['active', 'trialing'].includes(subscription.status)
      && Date.parse(subscription.current_period_end) > Date.now();
    return pricingPlans.find(plan => plan.id === (paid ? subscription.plan_id : 'free')) || null;
  };

  const isSubscriptionActive = (): boolean => {
    return !!subscription && ['active', 'trialing'].includes(subscription.status)
      && Date.parse(subscription.current_period_end) > Date.now();
  };

  const isSubscriptionTrialing = (): boolean => {
    return subscription?.status === 'trialing';
  };

  const isSubscriptionPastDue = (): boolean => {
    return subscription?.status === 'past_due';
  };

  const canAccessFeature = (feature: string): boolean => {
    const currentPlan = getCurrentPlan();
    if (!currentPlan) return false;

    switch (feature) {
      case 'advanced_ai':
        return currentPlan.includesAdvancedAI || false;
      case 'custom_training':
        return currentPlan.includesCustomTraining || false;
      case 'priority_support':
        return currentPlan.includesPrioritySupport || false;
      case 'sso':
        return currentPlan.includesSSO || false;
      case 'enterprise_features':
        return currentPlan.includesEnterpriseFeatures || false;
      default:
        return false;
    }
  };

  const getUsagePercentage = (metric: keyof UsageMetrics): number => {
    if (!usageMetrics) return 0;
    
    const current = usageMetrics[metric];
    const currentPlan = getCurrentPlan();
    if (!currentPlan) return 0;

    let limit = 0;
    switch (metric) {
      case 'projects_count':
        limit = currentPlan.maxProjects || 0;
        break;
      case 'storage_used':
        limit = currentPlan.maxStorage || 0;
        break;
      case 'ai_queries_used':
        limit = currentPlan.maxAIQueries || 0;
        break;
      case 'users_count':
        limit = currentPlan.maxUsers || 0;
        break;
      default:
        return 0;
    }

    return limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  };

  const value: PaymentContextType = {
    refreshBilling,
    isOpeningCheckout: createCheckoutSessionMutation.isPending,
    isOpeningPortal: openBillingPortalMutation.isPending,
    // State
    subscription,
    isLoadingSubscription,
    pricingPlans,
    isLoadingPlans,
    invoices,
    paymentMethods,
    isLoadingInvoices,
    isLoadingPaymentMethods,
    usageMetrics,
    isLoadingUsage,
    
    // Actions
    createCheckoutSession,
    openBillingPortal,
    cancelSubscription,
    reactivateSubscription,
    updateSubscription,
    contactSales,
    
    // Utilities
    getCurrentPlan,
    isSubscriptionActive,
    isSubscriptionTrialing,
    isSubscriptionPastDue,
    canAccessFeature,
    getUsagePercentage,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
      <Dialog open={aiAccessPrompt !== null} onOpenChange={open => { if (!open) setAiAccessPrompt(null); }}>
        <DialogContent
          overlayClassName="bg-black/50 backdrop-blur-[2px]"
          className="w-[calc(100vw-2rem)] max-w-md gap-0 overflow-hidden rounded-xl border-border/70 bg-card p-0 shadow-2xl"
        >
          <DialogHeader className="flex-row items-start gap-4 space-y-0 p-6 pr-12 text-left sm:p-7 sm:pr-12">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              {aiAccessPrompt === 'sign-in' ? <LockKeyhole className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
            </div>
            <div className="min-w-0 space-y-2">
              <DialogTitle className="text-base leading-6">
                {aiAccessPrompt === 'sign-in' ? 'Sign in to use AI' : 'Unlock Synkro AI'}
              </DialogTitle>
              <DialogDescription className="whitespace-normal break-words text-sm leading-6">
                {aiAccessPrompt === 'sign-in'
                  ? 'Sign in to check your plan and continue using Synkro AI.'
                  : 'AI assistants, summaries, and generation are included with Pro and Teams. Your Free workspace and its non-AI features stay available.'}
              </DialogDescription>
            </div>
          </DialogHeader>
          <DialogFooter className="flex-row items-center justify-between border-t border-border/70 bg-muted/20 px-6 py-4 sm:space-x-0 sm:px-7">
            <DialogClose asChild>
              <Button variant="ghost" className="text-muted-foreground">Maybe later</Button>
            </DialogClose>
            {aiAccessPrompt === 'sign-in' ? (
              <Button asChild><Link to="/auth" onClick={() => setAiAccessPrompt(null)}>Sign in</Link></Button>
            ) : (
              <Button asChild><Link to="/pricing" onClick={() => setAiAccessPrompt(null)}>See plans</Link></Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PaymentContext.Provider>
  );
};

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};
